import { useQuery } from "@tanstack/react-query";
import { checkSubscriptionStatus } from "../../../services/notificationService";
import { useState, useEffect } from "react";

interface SubscriptionStatusOptions {
  email: string;
  sessionId?: string;
  subject?: string;
  grade: string;
  enabled?: boolean;
}

/**
 * Custom hook to fetch and cache subscription status using React Query
 * @param options - Parameters needed to check subscription status
 * @returns Query result with subscription status, type, and helper methods
 */
export const useSubscriptionStatus = ({
  email,
  sessionId,
  subject,
  grade,
  enabled = true,
}: SubscriptionStatusOptions) => {
  // State to track subscription status from local storage while query loads
  const [localStorageStatus, setLocalStorageStatus] = useState<{
    isSubscribed: boolean;
    type: "session" | "subject" | "grade" | null;
  }>({
    isSubscribed: false,
    type: null,
  });

  // Load initial state from localStorage to avoid flickers
  useEffect(() => {
    if (!email || !sessionId) return;

    try {
      const savedStatus = localStorage.getItem(
        `notification_${email}_${sessionId}`
      );
      const savedType = localStorage.getItem(
        `notification_type_${email}_${sessionId}`
      );

      if (savedStatus === "true" && savedType) {
        setLocalStorageStatus({
          isSubscribed: true,
          type: savedType as "session" | "subject" | "grade",
        });
      } else {
        setLocalStorageStatus({
          isSubscribed: false,
          type: null,
        });
      }
    } catch (error) {
      console.warn("Could not access localStorage:", error);
    }
  }, [email, sessionId]);

  // Use React Query to fetch and cache subscription status
  const query = useQuery({
    queryKey: ["subscriptionStatus", email, sessionId, subject, grade],
    queryFn: async () => {
      // Only run if we have the required parameters
      if (!email || !grade) {
        throw new Error("Email and grade are required");
      }

      const result = await checkSubscriptionStatus(email, {
        sessionId,
        subject,
        grade,
      });

      // Update localStorage with the latest data
      if (sessionId) {
        try {
          localStorage.setItem(
            `notification_${email}_${sessionId}`,
            result.isSubscribed ? "true" : "false"
          );

          if (result.isSubscribed && result.subscriptionType) {
            localStorage.setItem(
              `notification_type_${email}_${sessionId}`,
              result.subscriptionType
            );
          } else {
            localStorage.removeItem(`notification_type_${email}_${sessionId}`);
          }
        } catch (error) {
          console.warn("Could not save to localStorage:", error);
        }
      }

      return result;
    },
    enabled: enabled && !!email && !!grade,
    staleTime: 60 * 1000, // Consider data fresh for 1 minute
    gcTime: 5 * 60 * 1000, // Keep data in cache for 5 minutes
    retry: 1, // Only retry once on failure
  });

  // Helper function to determine subscription type from query result
  const getSubscriptionType = () => {
    if (!query.data?.isSubscribed) return null;

    const { subscriptions } = query.data;

    if (
      sessionId &&
      subscriptions?.bySession &&
      Object.keys(subscriptions.bySession).includes(sessionId)
    ) {
      return "session";
    } else if (
      subject &&
      subscriptions?.bySubject &&
      subscriptions.bySubject[subject]
    ) {
      return "subject";
    } else if (subscriptions?.byGrade && subscriptions.byGrade[grade]) {
      return "grade";
    } else if (query.data.subscriptionType) {
      return query.data.subscriptionType;
    }

    return null;
  };

  // While loading, use localStorage values to avoid UI flickering
  return {
    ...query,
    // Override isLoading to provide immediate feedback based on localStorage
    isSubscribed: query.data
      ? query.data.isSubscribed
      : localStorageStatus.isSubscribed,
    // Get the most accurate subscription type
    subscriptionType: query.data
      ? getSubscriptionType()
      : localStorageStatus.type,
    // Provide original data for advanced usage
    subscriptionData: query.data,
  };
};
